import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import './STOImport.css';

const RecallImport = () => {
  const navigate = useNavigate();
  const { user, isAdmin, fetchWithAuth, getToken } = useAuth();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    setLocationsLoading(true);
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (!res.ok) throw new Error('Failed to load locations');
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setLocations(list);
      if (list.length > 0) {
        setSelectedLocationId((prev) => (prev === '' ? String(list[0].id) : prev));
      }
    } catch (err) {
      setError('Could not load consignment locations. ' + (err.message || ''));
    } finally {
      setLocationsLoading(false);
    }
  }, [isAdmin, fetchWithAuth]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const name = selectedFile.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setError('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setFile(selectedFile);
    setFileName(selectedFile.name);
    setError('');

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        setPreview(jsonData.slice(0, 10));
      } catch (err) {
        setError('Error reading file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    if (isAdmin) {
      if (!selectedLocationId) {
        setError('Please select a consignment location for this Recall.');
        return;
      }
    } else {
      if (!user?.consignmentLocationId) {
        setError('You do not have a consignment location assigned. Contact an administrator.');
        return;
      }
    }

    setIsUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (isAdmin) {
        formData.append('consignment_location_id', selectedLocationId);
      } else {
        formData.append('consignment_location_id', String(user.consignmentLocationId));
      }

      const token = getToken();
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/recall/upload', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = 'Upload failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || `Server error: ${response.status}`;
        } catch {
          errorMessage = `Server error: ${response.status}. Please check if the server is running.`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.success) {
        alert(`Success! ${result.message}`);
        navigate('/');
      } else {
        throw new Error(result.message || 'Import failed');
      }
    } catch (err) {
      console.error('Recall upload error:', err);
      setError(err.message || 'Failed to upload file.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Layout>
      <div className="sto-import-container">
        <div className="page-header">
          <button className="back-button" onClick={() => navigate('/')}>
            ← Back to Main Menu
          </button>
          <h1>Recall Import</h1>
        </div>

        <div className="content">
          <div className="upload-section">
            <p style={{ marginBottom: 16, color: '#374151', fontSize: 14 }}>
              Use the same Excel format as STO (Material, Description, Document Header, Material Document, Posting Date, <strong>Quantity</strong>). The only difference: <strong>Quantity must be negative</strong> for recall items.
            </p>
            <div className="upload-box">
              <input
                type="file"
                id="recall-file-input"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="recall-file-input" className="upload-label">
                <div className="upload-icon">📁</div>
                <div className="upload-text">
                  {fileName || 'Click to select Excel file'}
                </div>
              </label>
            </div>

            {isAdmin ? (
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="recall-consignment-location">Consignment location *</label>
                <select
                  id="recall-consignment-location"
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  disabled={locationsLoading}
                  style={{ width: '100%', maxWidth: '400px', padding: '8px 12px', marginTop: '4px' }}
                >
                  <option value="">Select location...</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location ${loc.id}`}
                    </option>
                  ))}
                </select>
                {locationsLoading && <span style={{ marginLeft: '8px' }}>Loading locations...</span>}
              </div>
            ) : (
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Consignment location</label>
                <div style={{ padding: '8px 0', fontWeight: 500 }}>
                  {user?.consignmentLocationName || (user?.consignmentLocationId ? `Location #${user.consignmentLocationId}` : 'Not assigned')}
                </div>
                {!user?.consignmentLocationId && (
                  <p style={{ color: '#c53030', marginTop: '4px', fontSize: '14px' }}>
                    You need a location assigned to import Recall. Contact an administrator.
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="error-message">
                <strong>Error:</strong> {error}
                <br />
                <small style={{ marginTop: '8px', display: 'block' }}>
                  Ensure the file has the same columns as STO and that the <strong>Quantity</strong> column contains negative values.
                </small>
              </div>
            )}

            {preview && (
              <div className="preview-section">
                <h3>File Preview (first 10 rows)</h3>
                <div className="preview-table">
                  <table>
                    <thead>
                      <tr>
                        {preview[0]?.map((header, idx) => (
                          <th key={idx}>{header || `Column ${idx + 1}`}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.slice(1).map((row, rowIdx) => (
                        <tr key={rowIdx}>
                          {row.map((cell, cellIdx) => (
                            <td key={cellIdx}>{cell ?? ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {file && (
              <button
                className="upload-button"
                onClick={handleUpload}
                disabled={isUploading || (isAdmin ? !selectedLocationId : !user?.consignmentLocationId) || locationsLoading}
              >
                {isUploading ? 'Uploading...' : 'Upload & Import Recall'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RecallImport;
